import { useState, useRef, useEffect, forwardRef } from "react";
import PropTypes from "prop-types";
import {
  getInputBorderClass,
  getInputBackgroundClass,
  isInputActive,
} from "@/helper/input_styles";
import BaseInput from "@/components/common/inputs/Base_Input";

const TextEditor = forwardRef(
  ({ label, placeholder, value = "", onChange }, ref) => {
    const [isFocused, setIsFocused] = useState(false);
    const [isEmpty, setIsEmpty] = useState(true);
    const editorRef = useRef(null);
    const containerRef = useRef(null);
    const timeoutRef = useRef(null);

    // Expose the editor instance through ref
    useEffect(() => {
      if (ref) {
        ref.current = {
          getEditor: () => editorRef.current,
          focus: () => editorRef.current.focus(),
        };
      }
    }, [ref]);

    // Set initial content
    useEffect(() => {
      if (
        editorRef.current &&
        value !== undefined &&
        editorRef.current.innerHTML !== value
      ) {
        editorRef.current.innerHTML = value || "";
      }
      const currentContent = editorRef.current
        ? editorRef.current.innerHTML
        : value || "";
      const hasContent =
        currentContent &&
        currentContent !== "<br>" &&
        currentContent !== "<div><br></div>" &&
        currentContent.trim() !== "";
      setIsEmpty(!hasContent);
    }, [value]); // Keep 'value' as the dependency

    // Debounced onChange handler
    const handleChange = () => {
      const content = editorRef.current.innerHTML;
      const isContentEmpty =
        !content ||
        content === "<br>" ||
        content === "<div><br></div>" ||
        content.trim() === "";
      setIsEmpty(isContentEmpty);

      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        onChange(content);
      }, 300);
    };

    // Use shared styling utilities
    const isActive = isInputActive(!isEmpty, isFocused);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target)
        ) {
          setIsFocused(false);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("touchend", handleClickOutside);

      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("touchend", handleClickOutside);
      };
    }, []);

    // Format selection with bold or underline
    const formatText = (command) => {
      editorRef.current.focus();

      document.execCommand(command, false, null);
      handleChange();
    };

    return (
      <BaseInput label={label} isActive={isActive}>
        <div
          ref={containerRef}
          className={`
          ${getInputBorderClass(isActive)}
          ${getInputBackgroundClass(isActive)}
          rounded-md relative transition-all duration-150 ease-in-out w-full
        `}
          data-testid="editor-wrapper">
          {/* Show placeholder when empty (regardless of focus) - always gray-400 */}
          {isEmpty && placeholder && (
            <div className="absolute top-8 left-3 text-gray-400 pointer-events-none z-10">
              {placeholder}
            </div>
          )}

          {/* Editable content area */}
          <div
            ref={editorRef}
            contentEditable
            className="min-h-[100px] pt-8 px-3 pb-10 outline-none w-full"
            onInput={handleChange}
            onFocus={() => {
              setIsFocused(true);
            }}
            onBlur={() => {
              setTimeout(() => {
                if (
                  containerRef.current &&
                  !containerRef.current.contains(document.activeElement)
                ) {
                  setIsFocused(false);
                }
              }, 100);
            }}
            data-testid="text-editor"
          />

          {/* Formatting toolbar with B/U format */}
          <div className="absolute bottom-2 right-2 flex items-center z-10">
            <div className="text-gray-600 flex items-center">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  formatText("bold");
                }}
                className="font-bold hover:text-primary focus:outline-none">
                B
              </button>
              <span className="mx-0.5">/</span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  formatText("underline");
                }}
                className="hover:text-primary focus:outline-none">
                U
              </button>
            </div>
          </div>
        </div>
      </BaseInput>
    );
  }
);

TextEditor.displayName = "TextEditor";

TextEditor.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
};

export default TextEditor;
